import {
  CardAvatar,
  CardListEmpty,
  CardRow,
  CardRowDeleteButton,
} from '@/components/data-table/card-list';
import {
  ClearFiltersButton,
  MultiSelectFilter,
  SearchInput,
} from '@/components/data-table/filters';
import { PaginationFooter } from '@/components/data-table/PaginationFooter';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { formatPrice } from '@/lib/format';
import { getOrderStatusLabel } from '@/lib/order-status';
import type { Order } from '@octave/contracts';
import { format } from 'date-fns';
import Fuse from 'fuse.js';
import { Package, User2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ORDER_STATUS_OPTIONS = [
  { value: 'pending', label: 'En cours' },
  { value: 'completed', label: 'Terminée' },
];

function getStatusDotColor(status: string) {
  switch (status.toLowerCase()) {
    case 'pending':
      return 'bg-amber-500';
    case 'completed':
      return 'bg-emerald-500';
    default:
      return 'bg-slate-400';
  }
}

interface DataTableProps {
  data: Order[];
  statusFilters: string[];
  onStatusFiltersChange: (filters: string[]) => void;
  /** Octave admins only: deletion action on each row. */
  onDelete?: (order: Order) => void;
}

export function DataTable({
  data,
  statusFilters,
  onStatusFiltersChange,
  onDelete,
}: DataTableProps) {
  const [searchText, setSearchText] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);

  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();

  const fuse = useMemo(
    () =>
      new Fuse(data, {
        keys: [
          'orderNumber',
          'createdBy.firstName',
          'createdBy.lastName',
          'items.name',
        ],
        threshold: 0.3,
      }),
    [data]
  );

  // Filter data
  const filteredData = useMemo(() => {
    let result = data;

    // Search
    if (searchText) {
      result = fuse.search(searchText).map((r) => r.item);
    }

    // Status filter
    if (statusFilters.length > 0) {
      result = result.filter((order) =>
        statusFilters.includes(order.status.toLowerCase())
      );
    }

    return result;
  }, [data, searchText, statusFilters, fuse]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const paginatedData = filteredData.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const handleStatusFilterChange = (value: string, checked: boolean) => {
    const newFilters = checked
      ? [...statusFilters, value]
      : statusFilters.filter((s) => s !== value);
    onStatusFiltersChange(newFilters);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchText('');
    onStatusFiltersChange([]);
    setPage(1);
  };

  const hasActiveFilters = searchText || statusFilters.length > 0;

  return (
    <div className="space-y-5">
      {/* Filters bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <SearchInput
          placeholder="Rechercher une commande..."
          value={searchText}
          onChange={handleSearchChange}
        />
        <MultiSelectFilter
          label="Statut"
          options={ORDER_STATUS_OPTIONS}
          selected={statusFilters}
          onToggle={handleStatusFilterChange}
        />
        {hasActiveFilters && <ClearFiltersButton onClick={handleClearFilters} />}
      </div>

      {/* Cards list */}
      <div className="space-y-2">
        {paginatedData.length ? (
          paginatedData.map((order) => {
            const clientName = order.createdBy
              ? `${order.createdBy.firstName || ''} ${order.createdBy.lastName || ''}`.trim()
              : null;
            const displayItems = order.items.slice(0, 3);
            const remainingCount = order.items.length - 3;
            const total = order.items.reduce(
              (sum, item) => sum + item.total,
              0
            );
            const recurringTotal = order.items
              .filter((item) => item.isRecurring)
              .reduce((sum, item) => sum + item.total, 0);

            return (
              <CardRow
                key={order.id}
                onClick={() => navigate(`/o/${tenantSlug}/orders/${order.id}`)}
              >
                <CardAvatar
                  initials={
                    clientName
                      ? `${(order.createdBy?.firstName || '').charAt(0)}${(
                          order.createdBy?.lastName || ''
                        ).charAt(0)}`
                      : null
                  }
                  fallbackIcon={Package}
                />

                {/* Main info */}
                <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr] gap-x-6 items-center">
                  {/* Col 1 — Ref + Client */}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 tabular-nums tracking-wide">
                      {order.orderNumber}
                    </p>
                    {clientName ? (
                      <p className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5">
                        <User2 className="h-3 w-3 shrink-0" />
                        {clientName}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-400 mt-0.5">-</p>
                    )}
                  </div>

                  {/* Col 2 — Articles */}
                  <div className="min-w-0 hidden sm:flex items-center gap-1.5 flex-wrap">
                    {displayItems.map((item) => (
                      <span
                        key={item.id}
                        className="inline-flex items-center bg-slate-100 text-slate-600 rounded-full px-2.5 py-0.5 text-xs truncate max-w-[140px]"
                      >
                        {item.quantity > 1 ? `${item.quantity}x ` : ''}
                        {item.name}
                      </span>
                    ))}
                    {remainingCount > 0 && (
                      <span className="text-xs text-slate-400 font-medium">
                        +{remainingCount}
                      </span>
                    )}
                  </div>

                  {/* Col 3 — Total + Date */}
                  <div className="min-w-0 text-right hidden sm:block">
                    <p className="text-sm font-semibold text-slate-900">
                      {formatPrice(total)}
                    </p>
                    <div className="flex items-center justify-end gap-2 mt-0.5">
                      {recurringTotal > 0 && (
                        <span className="text-xs text-emerald-600 font-medium">
                          {formatPrice(recurringTotal)}
                          /mois
                        </span>
                      )}
                      <span className="text-xs text-slate-400">
                        {format(new Date(order.createdAt), 'dd/MM/yyyy')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Status dot */}
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className={`shrink-0 h-2.5 w-2.5 rounded-full ${getStatusDotColor(order.status)}`}
                      />
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>{getOrderStatusLabel(order.status)}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {onDelete && (
                  <CardRowDeleteButton
                    label={`Supprimer la commande ${order.orderNumber}`}
                    onClick={() => onDelete(order)}
                  />
                )}
              </CardRow>
            );
          })
        ) : (
          <CardListEmpty icon={Package} />
        )}
      </div>

      <PaginationFooter
        page={page}
        pageCount={totalPages}
        summary={`${filteredData.length} commandes`}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        onPageChange={setPage}
      />
    </div>
  );
}
